import { createElement } from 'react'
import { Form, Head } from '@inertiajs/react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { DashboardShell } from '@/layouts/dashboard-shell'
import { organizationSubscribePortalPath } from '@/routes'
import type { OrganizationsSubscriptionsShow } from '@/types'

export default function OrganizationSubscriptionsShow({
  organization,
  repositories,
  stripePublishableKey,
  stripePricingTableId,
  subscriptionStatus,
  hasStripeCustomer,
  isAdmin,
}: OrganizationsSubscriptionsShow) {
  const canShowPricingTable =
    Boolean(isAdmin && stripePublishableKey && stripePricingTableId) &&
    subscriptionStatus !== 'active'

  return (
    <>
      <Head title={`${organization.name} — Billing`}>
        {canShowPricingTable ? <script async src="https://js.stripe.com/v3/pricing-table.js" /> : null}
      </Head>
      <DashboardShell organization={organization} repositories={repositories} title="Billing">
        <div className="mx-auto w-full max-w-3xl space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Subscription status</CardTitle>
              <CardDescription>
                Git Digest needs an active subscription to send digests for this workspace.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex items-center gap-3">
              <Badge variant={subscriptionStatus === 'active' ? 'default' : 'destructive'}>
                {subscriptionStatus ?? 'no subscription'}
              </Badge>
              {hasStripeCustomer && isAdmin ? (
                <Form method="post" action={organizationSubscribePortalPath(organization.id)}>
                  {({ processing }) => (
                    <Button type="submit" variant="outline" size="sm" disabled={processing}>
                      {processing ? 'Opening…' : 'Manage billing'}
                    </Button>
                  )}
                </Form>
              ) : null}
            </CardContent>
          </Card>

          {canShowPricingTable && isAdmin ? (
            <Card>
              <CardHeader>
                <CardTitle>Choose a plan</CardTitle>
              </CardHeader>
              <CardContent>
                {createElement('stripe-pricing-table', {
                  'pricing-table-id': stripePricingTableId,
                  'publishable-key': stripePublishableKey,
                  'client-reference-id': organization.id,
                })}
              </CardContent>
            </Card>
          ) : null}

          {!isAdmin ? (
            <p className="text-muted-foreground text-sm">Only workspace admins can manage billing.</p>
          ) : null}
        </div>
      </DashboardShell>
    </>
  )
}
